import React from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import type { Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import { AgentCard } from '../../../common/ui/agent-card/index.tsx';
import { getPaginationQueryInput } from '../../../common/utils/agent-query-helper/index.ts';
import { useAgentUrlActions } from '../../../controllers/agent-url-actions/index.tsx';
import { useBrowseAgentsUIStore } from '../../../controllers/browse-agents-ui/index.tsx';
import { isAutodevTemplateAgent } from '../../../controllers/browse-agents-ui/utils.ts';
import AgentDeleteConfirm from '../agent-delete-confirm/index.tsx';
import { NoCreatedAgentsEmptyState } from '../browse-agents-content/empty-states/no-created.tsx';
import { NoStarredAgentsEmptyState } from '../browse-agents-content/empty-states/no-starred.tsx';

import { AgentCategorySection, type RenderAgentCard } from './agent-category-section/index.tsx';
import messages from './messages.ts';
const styles = cssMap({
	agentsListFullscreen: {
		paddingTop: token('space.200'),
		overflow: 'auto',
		paddingInline: token('space.200'),
	},
});

type AgentsListFullscreenProps = {
	searchValue: string;
	onClickChat: (agent: Agent) => void;
} & AssistanceServiceInputParam;

/**
 * @private
 * @deprecated Use BrowseAgentsFullscreenNew instead. See thread for more info: https://atlassian.slack.com/archives/C08UY102KSA/p1779865548308599
 */
export const AgentsListFullscreen = ({
	assistanceServiceParams,
	searchValue,
	onClickChat,
}: AgentsListFullscreenProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const { onEditAgent, onCopyAgent, onDuplicateAgent } = useAgentUrlActions({
		siteId: assistanceServiceParams.siteId,
		source: 'agentCardFullscreen',
		xpcBridge: 'rovoAgents-browseAgentsModal',
	});

	const [_, { setAgentDeletion, setSelectedAgent }] = useBrowseAgentsUIStore();

	const renderAgentCard: RenderAgentCard = ({
		agent,
		agentRef,
		userPermissionsRef,
		hideDescription,
	}) => {
		return (
			<AgentCard
				key={agent.id}
				agentRef={agentRef}
				userPermissionsRef={userPermissionsRef}
				assistanceServiceParams={assistanceServiceParams}
				isAutodevTemplate={isAutodevTemplateAgent(agent)}
				siteId={assistanceServiceParams.siteId}
				onClickChat={() => onClickChat(agent)}
				onEditAgent={() => onEditAgent(agent.id)}
				onCopyAgent={() =>
					onCopyAgent({
						agentId: agent.id,
						agentIdentityAccountId: agent.identity_account_id,
					})
				}
				onDuplicateAgent={async () => {
					// TODO RAGE-2852 we can get agentARI guaranteed from useFragment
					await onDuplicateAgent(agent.id, agent.agentAri ?? '');
				}}
				onDeleteAgent={() => setAgentDeletion({ agent, status: 'none' })}
				onViewAgent={() => {
					setSelectedAgent(agent);
					sendAnalyticsEvent({
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'viewAgentButton',
						attributes: {
							agentId: agent.id,
							source: 'agentCardFullscreen',
						},
					});
				}}
				hideDescription={hideDescription}
			/>
		);
	};

	return (
		<Stack space="space.300" grow="fill" xcss={styles.agentsListFullscreen}>
			<AgentCategorySection
				assistanceServiceParams={assistanceServiceParams}
				sectionTitle={formatMessage(messages.favouritesSectionTitle)}
				input={getPaginationQueryInput({
					searchValue,
					tab: 'starred-agents',
				})}
				renderAgentCard={renderAgentCard}
				emptyState={
					fg('rovo_agent_fullscreen_empty_state') ? <NoStarredAgentsEmptyState /> : undefined
				}
			/>

			<AgentCategorySection
				assistanceServiceParams={assistanceServiceParams}
				sectionTitle={formatMessage(messages.myAgentsSectionTitle)}
				input={getPaginationQueryInput({
					searchValue,
					tab: 'my-agents',
				})}
				renderAgentCard={renderAgentCard}
				emptyState={
					fg('rovo_agent_fullscreen_empty_state') ? (
						<NoCreatedAgentsEmptyState
							siteId={assistanceServiceParams.siteId}
							navigationSource="agents-list-fullscreen-my-agents"
						/>
					) : undefined
				}
			/>

			<AgentDeleteConfirm assistanceServiceParams={assistanceServiceParams} />
		</Stack>
	);
};
